import { useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';

interface ToastProps {
  type: 'success' | 'error' | 'info';
  title: string;
  description?: string;
  onClose: () => void;
  duration?: number;
}

export const Toast = ({ type, title, description, onClose, duration = 3000 }: ToastProps) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const config = {
    success: {
      icon: <CheckCircle className="h-5 w-5" style={{ color: '#23F47C' }} />,
      bg: 'bg-[#141414]',
      border: 'border-[#23F47C]',
      iconBg: 'bg-[rgba(35,244,124,0.1)]',
    },
    error: {
      icon: <XCircle className="h-5 w-5 text-red-500" />,
      bg: 'bg-[#141414]',
      border: 'border-red-500',
      iconBg: 'bg-[rgba(239,68,68,0.1)]',
    },
    info: {
      icon: <AlertCircle className="h-5 w-5 text-blue-500" />,
      bg: 'bg-[#141414]',
      border: 'border-blue-500',
      iconBg: 'bg-[rgba(59,130,246,0.1)]',
    },
  };

  const { icon, bg, border, iconBg } = config[type];

  return (
    <div
      className={`fixed top-6 right-6 z-[9999] max-w-md w-full ${bg} ${border} border-l-4 rounded-xl shadow-2xl backdrop-blur-sm animate-slide-in-right`}
      role="alert"
      style={{
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
      }}
    >
      <div className="flex items-start gap-4 p-4">
        <div className={`flex-shrink-0 w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-[15px] leading-tight">{title}</h3>
          {description && (
            <p className="text-[#8F8F8F] text-[13px] mt-1 leading-relaxed">{description}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 text-[#5F5F5F] hover:text-white transition-colors p-1 rounded hover:bg-[#1F1F1F]"
          aria-label="Close notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      
      {/* Progress bar */}
      {duration > 0 && (
        <div className="h-1 bg-[#1F1F1F] overflow-hidden rounded-b-xl">
          <div
            className={`h-full ${type === 'success' ? 'bg-[#23F47C]' : type === 'error' ? 'bg-red-500' : 'bg-blue-500'}`}
            style={{
              animation: `shrink ${duration}ms linear forwards`,
            }}
          />
        </div>
      )}
    </div>
  );
};

// Toast manager for programmatic toast display
type ToastType = 'success' | 'error' | 'info';

let toastCallback: ((message: string, type: ToastType, description?: string) => void) | null = null;

export const setToastCallback = (callback: (message: string, type: ToastType, description?: string) => void) => {
  toastCallback = callback;
};

export const showToast = (message: string, type: ToastType = 'info', description?: string) => {
  if (toastCallback) {
    toastCallback(message, type, description);
  } else {
    // Fallback to console if toast system not initialized
    console.warn('Toast system not initialized. Message:', message);
  }
};
